/**
 * The Bloodline's arithmetic: what a dead run banks, and what a bought
 * unlock does to the next one.
 *
 * Two callers, two directions. `legacyEarned` is called **once, at the death
 * screen**, by the UI — the simulation itself never banks anything, because
 * the bank lives outside every run (`src/ui/bloodline.ts` owns the key).
 * The resolve/fold half is called from `initRun`: the purchases travel into a
 * run on `WorldConfig.bloodline`, exactly the door `raceId` and `leaderId`
 * use, which is what makes a bloodline run save, load, hash and replay like
 * any other — the config is saved verbatim and the folded numbers land in
 * `modsBase`, `res` and `unlockedPowers`, all of which were hashed long
 * before this file existed.
 *
 * **Canonical order, or replays lie.** Floating-point multiplication is not
 * associative, so the fold order must not depend on the order the UI happened
 * to list the ids in. `resolveBloodline` therefore returns defs in
 * *catalogue* order whatever order it was handed, and drops silently anything
 * unknown or orphaned of its prerequisite — a config written by a newer build
 * naming an unlock this build has never heard of should load as a run without
 * it, not refuse to load at all. The same argument as the save migration's
 * `??` defaults, applied to content.
 *
 * Kept pure on the `bindings.ts` model — no import of `world.ts` at runtime,
 * so `world.ts` can import this from `initRun` without closing a cycle.
 */

import {
  BLOODLINE_UNLOCKS,
  type BloodlineUnlockDef,
} from '../data/bloodline.ts';
import { RESOURCE_KEYS, type ResourceBundle } from './types.ts';

/**
 * What surviving to `wave` banks, before difficulty.
 *
 * Waves pay by the ten they sit in: the first ten are 1 Legacy each, the
 * second ten 2, the third 3, and so on without cap. Depth is the only input
 * on purpose — kills, buildings and resources are all things a long run does
 * more of anyway, and a formula with five terms is a formula nobody can hold
 * in their head at the death screen. Wave 22 is 36; wave 40 is 100.
 */
export function waveLegacy(wave: number): number {
  const w = Math.max(0, Math.floor(wave));
  let total = 0;
  for (let band = 0; band * 10 < w; band++) {
    const inBand = Math.min(10, w - band * 10);
    total += inBand * (band + 1);
  }
  return total;
}

/**
 * The bank credit for a finished run: depth, scaled by how heavy the night
 * was. `budgetMul` is the difficulty's wave-budget multiplier — the honest
 * measure of what the waves actually were, and already the number the
 * director scales by.
 */
export function legacyEarned(wave: number, budgetMul: number): number {
  return Math.round(waveLegacy(wave) * Math.max(0, budgetMul));
}

/**
 * The valid subset of `ids`, in catalogue order.
 *
 * One pass suffices because `requires` may only point earlier in the
 * catalogue — a property `tests/bloodline.test.ts` holds — so by the time a
 * def is considered, its whole ladder below it has been.
 */
export function resolveBloodline(ids: readonly string[] | undefined): BloodlineUnlockDef[] {
  if (!ids || ids.length === 0) return [];
  const asked = new Set(ids);
  const kept = new Set<string>();
  const out: BloodlineUnlockDef[] = [];
  for (const def of BLOODLINE_UNLOCKS) {
    if (!asked.has(def.id)) continue;
    if (def.requires && !kept.has(def.requires)) continue;
    kept.add(def.id);
    out.push(def);
  }
  return out;
}

/** The modifier bags to fold, in the one order they may ever be folded in. */
export function bloodlineModBags(ids: readonly string[] | undefined): Array<Record<string, number>> {
  const bags: Array<Record<string, number>> = [];
  for (const def of resolveBloodline(ids)) if (def.mods) bags.push(def.mods);
  return bags;
}

/**
 * The opening stockpile the bloodline adds, merged and key-checked. Added
 * unscaled by difficulty — the heirloom is what it is.
 */
export function bloodlineStart(ids: readonly string[] | undefined): Partial<ResourceBundle> {
  const out: Partial<ResourceBundle> = {};
  for (const def of resolveBloodline(ids)) {
    if (!def.start) continue;
    for (const k of RESOURCE_KEYS) {
      const g = def.start[k];
      if (g) out[k] = (out[k] ?? 0) + g;
    }
  }
  return out;
}

/** Power ids the bloodline adds to the opening kit, deduplicated. */
export function bloodlinePowers(ids: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const def of resolveBloodline(ids)) {
    for (const p of def.powers ?? []) if (!out.includes(p)) out.push(p);
  }
  return out;
}
